import type { UrlDto } from "@izicvs/dto";
import { t } from "@lingui/macro";
import { useMutation } from "@tanstack/react-query";

import { toast } from "@/client/hooks/use-toast";
import { axios } from "@/client/libs/axios";

export const printResume = async (data: { id: string }) => {
  const response = await axios.get<UrlDto>(`/resume/print/${data.id}`);

  return response.data;
};

/**
 * Fonction utilitaire pour télécharger un PDF sur tous les appareils
 * Stratégie adaptée pour iOS : redirection directe vers l'URL du PDF
 * @param url URL du PDF à télécharger
 * @param filename Nom de fichier suggéré (non utilisé sur mobile)
 */
export const downloadPdf = (url: string, filename?: string) => {
  // Détection des appareils mobiles (iOS/Android)
  const isMobile = /iphone|ipad|ipod|android/i.test(navigator.userAgent);

  if (isMobile) {
    // Notify the user
    toast({
      variant: "info",
      title: t`Downloading PDF`,
      description: t`Redirecting to the PDF...`,
    });

    // On mobile, a direct redirect is the most reliable method
    // This opens the PDF in the browser's built-in viewer
    window.location.href = url;
  } else {
    // Sur desktop : utiliser un lien avec attribut download
    const link = document.createElement("a");
    link.href = url;
    link.download = filename ?? "resume.pdf";
    link.target = "_blank";
    document.body.append(link);
    link.click();
    link.remove();
  }
};

export const usePrintResume = () => {
  const {
    error,
    isPending: loading,
    mutateAsync: printResumeFn,
  } = useMutation({
    mutationFn: printResume,
    onError: (error) => {
      const message = error.message;

      toast({
        variant: "error",
        title: t`Oops, the server returned an error.`,
        description: message,
      });
    },
  });

  return { printResume: printResumeFn, loading, error };
};
